import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  Paper,
  LinearProgress,
  Grid,
  Divider,
  Avatar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  AssignmentTurnedIn as AssignmentIcon,
  Flag as FlagIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { GET_AUDIT_FRAMEWORK_MIDDLE_PANEL } from "../../../../../apis/settingsApis/audit-apis/auditApis";
import { useAuditMilestones } from "../../hooks/useAuditMilestones";
import {
  calculateControlStats,
  calculateProgressPercentage,
  getActiveMilestoneIndex,
} from "../../utils/auditCalculations";

const SummaryTab = ({ processedAudit, isAuditorView = false }) => {
  const theme = useTheme();
  const [allControls, setAllControls] = useState([]);

  const { data: frameworkData } = useQuery(GET_AUDIT_FRAMEWORK_MIDDLE_PANEL, {
    variables: {
      audit_id: processedAudit?.id,
    },
    skip: !processedAudit?.id || !processedAudit?.audit_start_date,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const panel = frameworkData?.getAuditFrameworkMiddlePanel;
    if (!panel?.areaDetails) return;

    // Use Map to deduplicate controls by controlsId
    const controlsMap = new Map();
    panel.areaDetails.forEach((area) => {
      (area.criterias || []).forEach((criteria) => {
        (criteria.controls || []).forEach((control) => {
          if (control && control.controlsId) {
            const checks = control.checks || [];
            if (checks.length === 0) return;

            if (!controlsMap.has(control.controlsId)) {
              controlsMap.set(control.controlsId, {
                controlsId: control.controlsId,
                status: control.status,
                redFlag: control.redFlag,
              });
            }
          }
        });
      });
    });

    setAllControls(Array.from(controlsMap.values()));
  }, [frameworkData, isAuditorView]);

  // Use custom hooks and utilities
  const milestones = useAuditMilestones(processedAudit);
  const controlStats = calculateControlStats(processedAudit, allControls);
  const progressPercentage = calculateProgressPercentage(controlStats);
  const activeMilestoneIndex = getActiveMilestoneIndex(milestones);

  if (!processedAudit) {
    return (
      <Box variant="emptyState">
        <Typography variant="body2">
          No audit data available
        </Typography>
      </Box>
    );
  }

  const { totalControls, approved, flagged } = controlStats;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={3}>
        {/* Left Column: Progress & Details */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              height: "100%",
              borderRadius: "16px",
              boxShadow: theme.customColors.shadow.card,
              border: `1px solid ${theme.customColors.border.main}`,
              overflow: "hidden",
            }}
            variant="outlined"
          >
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Audit Progress
                </Typography>
              }
              sx={{
                borderBottom: `1px solid ${theme.customColors.border.subtle}`,
                bgcolor: theme.customColors.background.cardHeader,
                px: 3,
                py: 2,
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
                  <Box>
                    <Typography variant="h3" sx={{ color: theme.palette.primary.main, fontWeight: 800 }}>
                      {progressPercentage}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>
                      Overall Completion
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {approved} of {totalControls} Controls
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    height: 10,
                    borderRadius: "100px",
                    bgcolor: alpha(theme.customColors.border.subtle, 0.3),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${progressPercentage}%`,
                      background:
                        "linear-gradient(90deg, #4a7bff 0%, #7b9dff 100%)",
                      borderRadius: "100px",
                      transition: "width 0.5s ease",
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3, borderStyle: "dashed" }} />

              <Typography variant="overline" sx={{ mb: 2, display: "block", color: theme.customColors.text.secondary }}>
                Control Status Breakdown
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: "12px", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>Approved</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.palette.primary.main }}>
                      {approved}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: alpha(theme.customColors.status.error.main, 0.05), borderRadius: "12px", border: `1px solid ${alpha(theme.customColors.status.error.main, 0.2)}` }}>
                    <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>Flagged</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: theme.customColors.status.error.main }}>
                      {flagged}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Audit Stages */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              height: "100%",
              borderRadius: "16px",
              boxShadow: theme.customColors.shadow.card,
              border: `1px solid ${theme.customColors.border.main}`,
              overflow: "hidden",
            }}
            variant="outlined"
          >
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Audit Stages
                </Typography>
              }
              sx={{
                borderBottom: `1px solid ${theme.customColors.border.subtle}`,
                bgcolor: theme.customColors.background.cardHeader,
                px: 3,
                py: 2,
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ position: "relative" }}>
                {/* Vertical Connector Line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "19px",
                    top: "10px",
                    bottom: "30px",
                    width: "2px",
                    bgcolor: theme.customColors.border.subtle,
                    zIndex: 0,
                  }}
                />

                {milestones.map((milestone, index) => {
                  const isCompleted = milestone.isCompleted;
                  const isSkipped = milestone.isSkipped;
                  const isActive = index === activeMilestoneIndex && !isSkipped;
                  const isLast = index === milestones.length - 1;

                  return (
                    <Box
                      key={milestone.id}
                      sx={{
                        display: "flex",
                        gap: 2,
                        mb: isLast ? 0 : 4,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: isCompleted
                            ? theme.customColors.status.success.main
                            : (isActive || isSkipped)
                              ? "#3B82F6"
                              : theme.customColors.background.main,
                          border: `2px solid ${
                            isCompleted
                              ? theme.customColors.status.success.main
                              : (isActive || isSkipped)
                                ? "#3B82F6"
                                : theme.customColors.border.main
                          }`,
                          boxShadow: isActive ? `0 0 0 4px ${alpha("#3B82F6", 0.1)}` : "none",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon sx={{ fontSize: 20, color: "#fff" }} />
                        ) : isSkipped ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              fontSize: "1.25rem",
                            }}
                          >
                            −
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: isActive ? "#fff" : theme.customColors.text.secondary
                            }}
                          >
                            {index + 1}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ pt: 0.5 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isActive || isCompleted ? 700 : 500,
                            color: isActive
                              ? "#3B82F6 !important"
                              : theme.customColors.text.primary,
                            lineHeight: 1.2
                          }}
                        >
                          {milestone.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: theme.customColors.text.secondary,
                            textTransform: "none",
                            letterSpacing: "normal"
                          }}
                        >
                          {milestone.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummaryTab;
